import { Crepe } from '@milkdown/crepe';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import '@milkdown/crepe/theme/common/style.css';
import '@milkdown/crepe/theme/frame.css';

/** Shape of the lazily-imported milkdownHighlight module. */
type MilkdownHighlightModule = {
  installMilkdownHighlight: (crepe: Crepe) => void;
  addHighlightTopBarItem: (builder: {
    getGroup: (key: string) => { addItem: (...args: unknown[]) => unknown };
  }) => void;
};

/** Dynamically imports the highlight plugin, returning `null` if unavailable (e.g. SSR). */
const loadMilkdownHighlightModule = async (): Promise<MilkdownHighlightModule | null> => {
  try {
    return (await import('@/components/editor/milkdownHighlight')) as MilkdownHighlightModule;
  } catch {
    return null;
  }
};

/** Registers a `markdownUpdated` listener that calls `onChangeRef.current` while the component is still mounted. */
function attachCrepeMarkdownListener(
  crepe: Crepe,
  isMounted: () => boolean,
  onChangeRef: React.MutableRefObject<(markdown: string) => void>,
): void {
  crepe.on((listener) => {
    listener.markdownUpdated((_ctx, nextMarkdown) => {
      if (!isMounted()) {
        return;
      }
      onChangeRef.current(nextMarkdown);
    });
  });
}

export interface SkillCrepeBodyEditorProps {
  /** Initial markdown when the editor instance is created. */
  initialMarkdown: string;
  /** Change when loading a different template/version/new doc so Crepe remounts with correct default. */
  instanceKey: string;
  onMarkdownChange: (markdown: string) => void;
}

/** Imperative handle exposed to parent components via `forwardRef`. */
export type SkillCrepeBodyEditorHandle = {
  getMarkdown: () => string;
};

/**
 * Milkdown Crepe body editor — parity with CreateNotePage markdown editing (slash commands, toolbar).
 */
export const SkillCrepeBodyEditor = forwardRef<
  SkillCrepeBodyEditorHandle,
  SkillCrepeBodyEditorProps
>(({ initialMarkdown, instanceKey, onMarkdownChange }, ref) => {
  const editorHostRef = useRef<HTMLDivElement | null>(null);
  const crepeRef = useRef<Crepe | null>(null);
  const initialMarkdownRef = useRef(initialMarkdown);
  const onMarkdownChangeRef = useRef(onMarkdownChange);
  initialMarkdownRef.current = initialMarkdown;
  onMarkdownChangeRef.current = onMarkdownChange;

  useImperativeHandle(ref, () => ({
    getMarkdown: (): string => {
      const crepe = crepeRef.current;
      if (!crepe) {
        return '';
      }
      try {
        return crepe.getMarkdown();
      } catch {
        return '';
      }
    },
  }));

  useEffect(() => {
    if (!editorHostRef.current) {
      return undefined;
    }

    let mounted = true;
    let localCrepe: Crepe | null = null;

    const setupEditor = async (): Promise<void> => {
      let highlightModule: MilkdownHighlightModule | null = null;
      if (globalThis.process?.env?.NODE_ENV !== 'test') {
        highlightModule = await loadMilkdownHighlightModule();
      }
      const installMilkdownHighlight = highlightModule?.installMilkdownHighlight ?? null;
      const addHighlightTopBarItem = highlightModule?.addHighlightTopBarItem ?? null;

      if (!mounted || !editorHostRef.current) {
        return;
      }

      const crepe = new Crepe({
        root: editorHostRef.current,
        defaultValue: initialMarkdownRef.current,
        features: {
          [Crepe.Feature.TopBar]: true,
        },
        featureConfigs: {
          [Crepe.Feature.Placeholder]: {
            text: "Type '/' to open slash commands",
            mode: 'block',
          },
          [Crepe.Feature.TopBar]: {
            buildTopBar: (builder) => {
              addHighlightTopBarItem?.(
                builder as {
                  getGroup: (key: string) => { addItem: (...args: unknown[]) => unknown };
                },
              );
            },
          },
        },
      });

      localCrepe = crepe;
      crepeRef.current = crepe;
      installMilkdownHighlight?.(crepe);

      attachCrepeMarkdownListener(crepe, () => mounted, onMarkdownChangeRef);

      await crepe.create();
      if (!mounted) {
        return;
      }
      onMarkdownChangeRef.current(crepe.getMarkdown());
    };

    void setupEditor();

    return () => {
      mounted = false;
      const activeCrepe = localCrepe ?? crepeRef.current;
      crepeRef.current = null;
      if (activeCrepe) {
        void activeCrepe.destroy();
      }
    };
    // Only remount when switching documents (template/version/new). Do NOT depend on
    // `initialMarkdown` — the parent keeps `body` in sync with typing; including it would
    // destroy/recreate Crepe on every keystroke and steal focus.
  }, [instanceKey]);

  return (
    <div
      className="milkdown-host"
      ref={editorHostRef}
      data-testid="skill-crepe-host"
      aria-label="Skill body editor"
    />
  );
});

SkillCrepeBodyEditor.displayName = 'SkillCrepeBodyEditor';
